<template>
    <widget-wrapper
    extraClass="cards"
    :variant="variant"
    :title="t('home.widgets.cards.title')"
    :applyErrorState="applyErrorState"
    @redirectToFeature="navigateByName(routeList.CARDS_CONSULT_NAME)">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- slider -->
        <cards-slider
        v-if="!showLoader && getCardsList.length"
        :dashboardWidget="variant !== 'anchor'"
        :dashboardWidgetAnchor="variant === 'anchor'"
        :selectedCardId="selectedCard.id"
        :cardsList="getCardsList"
        :hideBalance="hideBalance"
        @clicked="selectedCard = getCardsList.find(obj => obj.id === $event)"/>
        <!-- Card Options -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && selectedCard" class="for-animation-to-work" :key="`selectedCard${selectedCard.id}`">
                <selected-card-info
                dashboardWidget
                hideCardDetail
                :dashboardWidgetAddButton="getMobileAppState"
                :selectedCard="selectedCard"/>
            </div>
        </transition>
        <empty-state-generic
        v-if="!showLoader && !getCardsList.length"
        illustration="emptyState/cards-generic"
        :hasButton="showNewCardButton"
        :title="t('home.widgets.cards.emptyStateTitle')"
        :description="showNewCardButton ? t('home.widgets.cards.emptyStateDescription') : t('home.widgets.cards.emptyStateDescription2')"
        :buttonLabel="showNewCardButton ? t('home.widgets.cards.emptyStateBtn') : null"
        @clicked="navigateByName(routeList.CARDS_REQUEST_NAME)"/>
    </widget-wrapper>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import CardsSlider from '@/components/generic/sliders/bankCards/Base'
import SelectedCardInfo from '@/components/features/cards/SelectedCardInfo'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue & Assets */
import { ref, onMounted } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { getCurrentRouteMenuItemInfo } from '@/assets/scripts/parsers/menu'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits(['hideMe'])

/* Props
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    variant: String,
    hideBalance: Boolean
})

/* mapActions & mapGetters */
const { requestCardsList } = mapActions(moduleMapper.HOME)
const { getCardsList } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const selectedCard = ref(null)
const showNewCardButton = ref(false)

/*************************************************
*                                                *
*                 Request Card List              *
*                                                *
*************************************************/
const fetchCardsList = async () => {
    showLoader.value = true
	applyErrorState.value = false

	try {
        await requestCardsList({ fetchLatestBalances: false })
        showLoader.value = false

		if (!getCardsList.value.length) {
            emit('hideMe')
            return
        }
        /* Setting Initial Selected Card */
		selectedCard.value = getCardsList.value[0]
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
	}
}

fetchCardsList()

onMounted(() => {
    const routeToGetInfo = routeList.CARDS_BASE_PATH
    const menuEntriesForAccelarator = getCurrentRouteMenuItemInfo(routeToGetInfo).children

    if (menuEntriesForAccelarator.find(obj => obj.label === 'cards_request') || menuEntriesForAccelarator.find(obj => obj.label === 'cards_activate')) {
        showNewCardButton.value = !!getCurrentRouteMenuItemInfo('/cards/request')
    }
})
</script>

<style lang="scss" scoped>
.for-animation-to-work {
    @include commonEnteringAnimation;
    width: 100%;
    margin-top: 24px;
}
</style>
